import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Space,
  Popconfirm,
  message,
  Card,
  Typography,
  Tooltip,
  Tag,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Input,
  Descriptions,
  Divider,
  Tabs,
  Alert,
  Badge,
  Avatar,
  List,
  Result,
  Image,
  Checkbox,
} from 'antd';
import {
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  FileOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  SoundOutlined,
  FileTextOutlined,
  FilterOutlined,
  ClearOutlined,
  DownloadOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  FolderOpenOutlined,
  DisconnectOutlined,
  FileUnknownOutlined,
  UserOutlined,
  HddOutlined,
  PieChartOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PagesWrapper from '../../../PagesWrapper';
import filesService from '../../../services/filesService';
import { getProfileImageUrl } from '../../../utils/urlUtils';
import styles from './FileStorage.module.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Category icons mapping
 */
const CategoryIcons = {
  Image: <FileImageOutlined />,
  Video: <VideoCameraOutlined />,
  Audio: <SoundOutlined />,
  Document: <FileTextOutlined />,
  Other: <FileOutlined />,
};

/**
 * Category colors for charts
 */
const CategoryColors = {
  Image: '#1890ff',
  Video: '#eb2f96',
  Audio: '#722ed1',
  Document: '#fa8c16',
  Other: '#8c8c8c',
};

/**
 * File Storage Management Page
 * Admin page for managing files, viewing storage health, and handling orphan/untracked/ghost files
 */
const FileStorage = () => {
  const { t } = useTranslation();
  
  // Data state
  const [files, setFiles] = useState([]);
  const [storageHealth, setStorageHealth] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  
  // Filters
  const [filters, setFilters] = useState({
    category: null,
    dateRange: null,
    searchTerm: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('files');
  
  // Modals
  const [selectedFile, setSelectedFile] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  
  // Ghost records selection
  const [selectedGhostRecords, setSelectedGhostRecords] = useState([]);
  const [ghostDeleteLoading, setGhostDeleteLoading] = useState(false);
  
  // Unassociated files selection
  const [selectedUnassociatedFiles, setSelectedUnassociatedFiles] = useState([]);
  const [unassociatedDeleteLoading, setUnassociatedDeleteLoading] = useState(false);

  /**
   * Fetch files from API
   */
  const fetchFiles = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
      };
      
      if (filters.category) params.category = filters.category;
      if (filters.dateRange && filters.dateRange[0]) {
        params.fromDate = filters.dateRange[0].toISOString();
      }
      if (filters.dateRange && filters.dateRange[1]) {
        params.toDate = filters.dateRange[1].toISOString();
      }
      if (filters.searchTerm) {
        params.searchTerm = filters.searchTerm;
      }

      const response = await filesService.getFiles(params);
      setFiles(response.items || []);
      setPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
      });
    } catch (error) {
      message.error(t('admin.fileStorage.fetchError', 'Failed to load files'));
      console.error('Failed to fetch files:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  /**
   * Fetch storage health
   */
  const fetchStorageHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const health = await filesService.getStorageHealth();
      setStorageHealth(health);
    } catch (error) {
      console.error('Failed to fetch storage health:', error);
      message.error(t('admin.fileStorage.healthError', 'Failed to load storage health'));
    } finally {
      setHealthLoading(false);
    }
  }, [t]);

  /**
   * Fetch settings
   */
  const fetchSettings = useCallback(async () => {
    try {
      const resp = await filesService.getStorageSettings();
      setSettings(resp);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchFiles(pagination.current, pagination.pageSize);
    fetchStorageHealth();
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle table pagination changes
   */
  const handleTableChange = (newPagination) => {
    fetchFiles(newPagination.current, newPagination.pageSize);
  };

  /**
   * Apply filters
   */
  const applyFilters = () => {
    fetchFiles(1, pagination.pageSize);
  };

  /**
   * Reset filters
   */
  const resetFilters = () => {
    setFilters({
      category: null,
      dateRange: null,
      searchTerm: '',
    });
    setTimeout(() => fetchFiles(1, pagination.pageSize), 0);
  };

  /**
   * View file detail
   */
  const viewFileDetail = async (file) => {
    try {
      const fullFile = await filesService.getFile(file.id);
      setSelectedFile(fullFile);
      setDetailModalVisible(true);
    } catch (error) {
      message.error(t('admin.fileStorage.detailError', 'Failed to load file details'));
    }
  };

  /**
   * Download file
   */
  const handleDownload = async (id, fileName) => {
    try {
      await filesService.downloadFileToBrowser(id, fileName);
    } catch (error) {
      message.error(t('admin.fileStorage.downloadError', 'Failed to download file'));
    }
  };

  /**
   * Cleanup orphaned records
   */
  const handleCleanupOrphans = async () => {
    setCleanupLoading(true);
    try {
      const result = await filesService.cleanupOrphanedRecords();
      message.success(result.message || `Cleaned up ${result.itemsDeleted} orphaned records`);
      setSelectedGhostRecords([]);
      fetchStorageHealth();
    } catch (error) {
      message.error(t('admin.fileStorage.cleanupError', 'Cleanup failed'));
    } finally {
      setCleanupLoading(false);
    }
  };

  /**
   * Delete a single ghost record
   */
  const handleDeleteGhostRecord = async (fileId) => {
    setGhostDeleteLoading(true);
    try {
      await filesService.deleteGhostRecord(fileId);
      message.success(t('admin.fileStorage.ghostFiles.deleteSuccess', 'Ghost record deleted successfully'));
      setSelectedGhostRecords(prev => prev.filter(id => id !== fileId));
      fetchStorageHealth();
    } catch (error) {
      message.error(t('admin.fileStorage.ghostFiles.deleteError', 'Failed to delete ghost record'));
    } finally {
      setGhostDeleteLoading(false);
    }
  };

  /**
   * Bulk delete selected ghost records
   */
  const handleBulkDeleteGhostRecords = async () => {
    if (selectedGhostRecords.length === 0) {
      message.warning(t('admin.fileStorage.ghostFiles.noSelection', 'No ghost records selected'));
      return;
    }
    
    setGhostDeleteLoading(true);
    try {
      const result = await filesService.bulkDeleteGhostRecords(selectedGhostRecords);
      message.success(result.message || `Deleted ${result.itemsDeleted} ghost record(s)`);
      setSelectedGhostRecords([]);
      fetchStorageHealth();
    } catch (error) {
      message.error(t('admin.fileStorage.ghostFiles.bulkDeleteError', 'Failed to delete ghost records'));
    } finally {
      setGhostDeleteLoading(false);
    }
  };

  /**
   * Toggle ghost record selection
   */
  const toggleGhostRecordSelection = (fileId) => {
    setSelectedGhostRecords(prev => {
      if (prev.includes(fileId)) {
        return prev.filter(id => id !== fileId);
      }
      return [...prev, fileId];
    });
  };

  /**
   * Select/deselect all ghost records
   */
  const toggleAllGhostRecords = (allRecords) => {
    if (selectedGhostRecords.length === allRecords.length) {
      setSelectedGhostRecords([]);
    } else {
      setSelectedGhostRecords(allRecords.map(r => r.file?.id).filter(Boolean));
    }
  };

  /**
   * Delete a single unassociated file
   */
  const handleDeleteUnassociatedFile = async (fileId) => {
    setUnassociatedDeleteLoading(true);
    try {
      await filesService.deleteUnassociatedFile(fileId);
      message.success(t('admin.fileStorage.orphanFiles.deleteSuccess', 'Unassociated file deleted successfully'));
      setSelectedUnassociatedFiles(prev => prev.filter(id => id !== fileId));
      fetchStorageHealth();
    } catch (error) {
      message.error(t('admin.fileStorage.orphanFiles.deleteError', 'Failed to delete unassociated file'));
    } finally {
      setUnassociatedDeleteLoading(false);
    }
  };

  /**
   * Bulk delete selected unassociated files
   */
  const handleBulkDeleteUnassociatedFiles = async () => {
    if (selectedUnassociatedFiles.length === 0) {
      message.warning(t('admin.fileStorage.orphanFiles.noSelection', 'No files selected'));
      return;
    }
    
    setUnassociatedDeleteLoading(true);
    try {
      const result = await filesService.bulkDeleteUnassociatedFiles(selectedUnassociatedFiles);
      message.success(result.message || `Deleted ${result.itemsDeleted} unassociated file(s)`);
      setSelectedUnassociatedFiles([]);
      fetchStorageHealth();
    } catch (error) {
      message.error(t('admin.fileStorage.orphanFiles.bulkDeleteError', 'Failed to delete unassociated files'));
    } finally {
      setUnassociatedDeleteLoading(false);
    }
  };

  /**
   * Toggle unassociated file selection
   */
  const toggleUnassociatedFileSelection = (fileId) => {
    setSelectedUnassociatedFiles(prev => {
      if (prev.includes(fileId)) {
        return prev.filter(id => id !== fileId);
      }
      return [...prev, fileId];
    });
  };

  /**
   * Select/deselect all unassociated files
   */
  const toggleAllUnassociatedFiles = (allFiles) => {
    if (selectedUnassociatedFiles.length === allFiles.length) {
      setSelectedUnassociatedFiles([]);
    } else {
      setSelectedUnassociatedFiles(allFiles.map(f => f.id).filter(Boolean));
    }
  };

  /**
   * Cleanup untracked files
   */
  const handleCleanupUntracked = async () => {
    setCleanupLoading(true);
    try {
      const result = await filesService.cleanupUntrackedFiles();
      message.success(result.message || `Cleaned up ${result.itemsDeleted} untracked files`);
      fetchStorageHealth();
    } catch (error) {
      message.error(t('admin.fileStorage.cleanupError', 'Cleanup failed'));
    } finally {
      setCleanupLoading(false);
    }
  };

  /**
   * Get category style class
   */
  const getCategoryClass = (category) => {
    switch (category) {
      case 'Image': return styles.categoryImage;
      case 'Video': return styles.categoryVideo;
      case 'Audio': return styles.categoryAudio;
      case 'Document': return styles.categoryDocument;
      default: return styles.categoryOther;
    }
  };

  /**
   * Format timestamp
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  /**
   * Get relative time
   */
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return formatTimestamp(timestamp);
  };

  /**
   * Get file URL
   */
  const getFileUrl = (file) => {
    if (!file?.relativeUrl) return null;
    // Handle both development and production environments
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
    return `${baseUrl}${file.relativeUrl}`;
  };

  /**
   * Check if file is previewable as image
   */
  const isPreviewableImage = (file) => {
    return file?.category === 'Image' && filesService.isImage(file?.contentType);
  };

  /**
   * Get storage health status
   */
  const getHealthStatus = () => {
    if (!storageHealth) return 'unknown';
    const { orphanedRecordsCount, untrackedFilesCount } = storageHealth;
    if (orphanedRecordsCount === 0 && untrackedFilesCount === 0) return 'healthy';
    if (orphanedRecordsCount > 10 || untrackedFilesCount > 10) return 'critical';
    return 'warning';
  };

  /**
   * Table columns configuration
   */
  const columns = [
    {
      title: t('admin.fileStorage.columns.category', 'Type'),
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category) => (
        <span className={`${styles.categoryBadge} ${getCategoryClass(category)}`}>
          {CategoryIcons[category] || CategoryIcons.Other}
          <span style={{ marginLeft: 4 }}>{category}</span>
        </span>
      ),
    },
    {
      title: t('admin.fileStorage.columns.fileName', 'File Name'),
      dataIndex: 'fileName',
      key: 'fileName',
      width: 500,
      ellipsis: true,
      render: (fileName, record) => (
        <Tooltip title={fileName}>
          <Space align="start">
            {isPreviewableImage(record) && (
              <Avatar
                size="small"
                shape="square"
                src={getFileUrl(record)}
                icon={<FileImageOutlined />}
              />
            )}
            <div className={styles.fileNameContainer}>
              <span className={styles.fileNamePrimary}>{fileName}</span>
              {record.storedFileName && (
                <span className={styles.storedFileName}>{record.storedFileName}</span>
              )}
            </div>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: t('admin.fileStorage.columns.size', 'Size'),
      dataIndex: 'fileSizeFormatted',
      key: 'size',
      width: 100,
      sorter: (a, b) => a.fileSizeBytes - b.fileSizeBytes,
    },
    {
      title: t('admin.fileStorage.columns.uploadedAt', 'Uploaded'),
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      width: 150,
      render: (date) => (
        <Tooltip title={formatTimestamp(date)}>
          <span>{getRelativeTime(date)}</span>
        </Tooltip>
      ),
      sorter: (a, b) => new Date(a.dateCreated) - new Date(b.dateCreated),
      defaultSortOrder: 'descend',
    },
    {
      title: t('admin.fileStorage.columns.uploadedBy', 'Uploaded By'),
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      width: 150,
      render: (uploadedBy) => uploadedBy ? (
        <Space>
          <Avatar 
            size="small" 
            src={getProfileImageUrl(uploadedBy)}
            icon={<UserOutlined />} 
          />
          <span>{uploadedBy.displayName || uploadedBy.email}</span>
        </Space>
      ) : (
        <Text type="secondary">-</Text>
      ),
    },
    {
      title: t('admin.fileStorage.columns.actions', 'Actions'),
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('common.view', 'View')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewFileDetail(record)}
            />
          </Tooltip>
          <Tooltip title={t('common.download', 'Download')}>
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record.id, record.fileName)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  /**
   * Render statistics dashboard
   */
  const renderStatistics = () => (
    <Row gutter={[16, 16]} className={styles.statsRow}>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={healthLoading}>
          <Statistic
            title={t('admin.fileStorage.stats.totalFiles', 'Total Files')}
            value={storageHealth?.totalFilesInDatabase || 0}
            prefix={<DatabaseOutlined style={{ color: '#1890ff' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={healthLoading}>
          <Statistic
            title={t('admin.fileStorage.stats.totalSize', 'Total Size')}
            value={storageHealth?.totalSizeFormatted || '0 bytes'}
            prefix={<HddOutlined style={{ color: '#52c41a' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={healthLoading}>
          <Statistic
            title={t('admin.fileStorage.stats.onDisk', 'Files on Disk')}
            value={storageHealth?.totalFilesOnDisk || 0}
            prefix={<FolderOpenOutlined style={{ color: '#722ed1' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card
          className={`${styles.statCard} ${storageHealth?.orphanedRecordsCount > 0 ? styles.statCardDanger : ''}`}
          loading={healthLoading}
        >
          <Statistic
            title={t('admin.fileStorage.stats.ghostFiles', 'Ghost Records')}
            value={storageHealth?.orphanedRecordsCount || 0}
            valueStyle={{ color: storageHealth?.orphanedRecordsCount > 0 ? '#ff4d4f' : '#52c41a' }}
            prefix={<DisconnectOutlined />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card
          className={`${styles.statCard} ${storageHealth?.unassociatedFilesCount > 0 ? styles.statCardInfo : ''}`}
          loading={healthLoading}
        >
          <Statistic
            title={t('admin.fileStorage.stats.orphanFiles', 'Orphan Files')}
            value={storageHealth?.unassociatedFilesCount || 0}
            valueStyle={{ color: storageHealth?.unassociatedFilesCount > 0 ? '#1890ff' : '#52c41a' }}
            prefix={<LinkOutlined />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card
          className={`${styles.statCard} ${storageHealth?.untrackedFilesCount > 0 ? styles.statCardWarning : ''}`}
          loading={healthLoading}
        >
          <Statistic
            title={t('admin.fileStorage.stats.untrackedFiles', 'Untracked Files')}
            value={storageHealth?.untrackedFilesCount || 0}
            valueStyle={{ color: storageHealth?.untrackedFilesCount > 0 ? '#faad14' : '#52c41a' }}
            prefix={<FileUnknownOutlined />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={healthLoading}>
          <div style={{ textAlign: 'center' }}>
            <div className={styles.healthIndicator}>
              {getHealthStatus() === 'healthy' && (
                <CheckCircleOutlined className={styles.healthGood} style={{ fontSize: 24 }} />
              )}
              {getHealthStatus() === 'warning' && (
                <WarningOutlined className={styles.healthWarning} style={{ fontSize: 24 }} />
              )}
              {getHealthStatus() === 'critical' && (
                <ExclamationCircleOutlined className={styles.healthDanger} style={{ fontSize: 24 }} />
              )}
              <Text strong style={{ marginLeft: 8 }}>
                {getHealthStatus() === 'healthy' && 'Healthy'}
                {getHealthStatus() === 'warning' && 'Needs Attention'}
                {getHealthStatus() === 'critical' && 'Critical'}
              </Text>
            </div>
            <Text type="secondary">Storage Health</Text>
          </div>
        </Card>
      </Col>
    </Row>
  );

  /**
   * Render category distribution
   */
  const renderCategoryDistribution = () => {
    if (!storageHealth?.filesByCategory) return null;
    
    const categories = storageHealth.filesByCategory;
    const totalFiles = Object.values(categories).reduce((a, b) => a + b, 0);
    
    return (
      <Card title={<Space><PieChartOutlined /> Category Distribution</Space>} size="small">
        <div className={styles.categoryChart}>
          {Object.entries(categories).map(([category, count]) => (
            <div key={category} className={styles.categoryItem}>
              <div className={styles.categoryLabel}>
                {CategoryIcons[category] || CategoryIcons.Other}
                <span style={{ marginLeft: 8 }}>{category}</span>
              </div>
              <div className={styles.categoryBar}>
                <div
                  className={styles.categoryBarFill}
                  style={{
                    width: `${(count / totalFiles) * 100}%`,
                    backgroundColor: CategoryColors[category] || CategoryColors.Other,
                  }}
                />
              </div>
              <Text style={{ minWidth: 60, textAlign: 'right' }}>
                {count} ({Math.round((count / totalFiles) * 100)}%)
              </Text>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  /**
   * Render filter panel
   */
  const renderFilterPanel = () => (
    <Card className={styles.filterPanel} size="small">
      <Row gutter={[16, 16]} align="bottom">
        <Col xs={24} sm={12} md={6}>
          <div className={styles.filterLabel}>Category</div>
          <Select
            style={{ width: '100%' }}
            placeholder="All Categories"
            allowClear
            value={filters.category}
            onChange={(value) => setFilters({ ...filters, category: value })}
          >
            {Object.keys(CategoryIcons).map(cat => (
              <Select.Option key={cat} value={cat}>
                <Space>{CategoryIcons[cat]} {cat}</Space>
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div className={styles.filterLabel}>Date Range</div>
          <RangePicker
            style={{ width: '100%' }}
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className={styles.filterLabel}>Search</div>
          <Input
            placeholder="Search by filename..."
            value={filters.searchTerm}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Space>
            <Button type="primary" onClick={applyFilters} icon={<SearchOutlined />}>
              Apply
            </Button>
            <Button onClick={resetFilters} icon={<ClearOutlined />}>
              Reset
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  /**
   * Render ghost files (orphaned records) tab
   */
  const renderGhostFilesTab = () => {
    const orphanedRecords = storageHealth?.orphanedRecords || [];
    const allSelected = orphanedRecords.length > 0 && selectedGhostRecords.length === orphanedRecords.length;
    const someSelected = selectedGhostRecords.length > 0 && selectedGhostRecords.length < orphanedRecords.length;
    
    return (
      <div>
        <Alert
          message={t('admin.fileStorage.ghostFiles.title', 'Ghost Records')}
          description={t(
            'admin.fileStorage.ghostFiles.description',
            'These are database records for files that no longer exist on disk. They may have been manually deleted or removed by system errors.'
          )}
          type="error"
          showIcon
          icon={<DisconnectOutlined />}
          style={{ marginBottom: 16 }}
        />
        
        {orphanedRecords.length === 0 ? (
          <Result
            status="success"
            icon={<CheckCircleOutlined className={styles.successState} />}
            title={t('admin.fileStorage.ghostFiles.empty', 'No Ghost Records Found')}
            subTitle="All database records have corresponding files on disk."
          />
        ) : (
          <>
            <div className={styles.issueHeader}>
              <div className={styles.issueTitle}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => toggleAllGhostRecords(orphanedRecords)}
                  style={{ marginRight: 12 }}
                />
                <DisconnectOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                <span className={`${styles.issueCount} ${styles.orphanCount}`}>
                  {orphanedRecords.length}
                </span>
                <Text>ghost records found</Text>
                {selectedGhostRecords.length > 0 && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {selectedGhostRecords.length} selected
                  </Tag>
                )}
              </div>
              <Space>
                {selectedGhostRecords.length > 0 && (
                  <Popconfirm
                    title={`Delete ${selectedGhostRecords.length} ghost record(s)?`}
                    description="This will delete the selected database records and create deletion logs for each."
                    onConfirm={handleBulkDeleteGhostRecords}
                    okText="Yes, delete selected"
                    okButtonProps={{ danger: true }}
                  >
                    <Button 
                      danger 
                      loading={ghostDeleteLoading} 
                      icon={<DeleteOutlined />}
                    >
                      Delete Selected ({selectedGhostRecords.length})
                    </Button>
                  </Popconfirm>
                )}
                <Popconfirm
                  title="Clean up all ghost records?"
                  description="This will delete all database records that don't have corresponding files."
                  onConfirm={handleCleanupOrphans}
                  okText="Yes, clean up all"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger loading={cleanupLoading} icon={<DeleteOutlined />}>
                    Clean Up All
                  </Button>
                </Popconfirm>
              </Space>
            </div>
            
            <List
              className={styles.issueList}
              dataSource={orphanedRecords}
              renderItem={(item) => (
                <div className={styles.issueItem}>
                  <Checkbox
                    checked={selectedGhostRecords.includes(item.file?.id)}
                    onChange={() => toggleGhostRecordSelection(item.file?.id)}
                    style={{ marginRight: 12 }}
                  />
                  <div className={styles.issueItemInfo} style={{ flex: 1 }}>
                    <div className={styles.issueFileName}>
                      {CategoryIcons[item.file?.category] || CategoryIcons.Other}
                      <div className={styles.fileNameContainer} style={{ marginLeft: 8 }}>
                        <span>{item.file?.fileName}</span>
                        <span className={styles.storedFileName}>{item.file?.storedFileName}</span>
                      </div>
                    </div>
                    <div className={styles.issueFileMeta}>
                      ID: {item.file?.id} • {item.file?.fileSizeFormatted} • {item.reason}
                    </div>
                  </div>
                  <Popconfirm
                    title="Delete this ghost record?"
                    description="This will remove the database record and create a deletion log."
                    onConfirm={() => handleDeleteGhostRecord(item.file?.id)}
                    okText="Yes, delete"
                    okButtonProps={{ danger: true }}
                  >
                    <Tooltip title={t('common.delete', 'Delete')}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        loading={ghostDeleteLoading}
                        size="small"
                      />
                    </Tooltip>
                  </Popconfirm>
                </div>
              )}
            />
          </>
        )}
      </div>
    );
  };

  /**
   * Render orphan files tab (files with no entity associations)
   */
  const renderOrphanFilesTab = () => {
    const unassociatedFiles = storageHealth?.unassociatedFiles || [];
    const allSelected = unassociatedFiles.length > 0 && selectedUnassociatedFiles.length === unassociatedFiles.length;
    const someSelected = selectedUnassociatedFiles.length > 0 && selectedUnassociatedFiles.length < unassociatedFiles.length;
    
    return (
      <div>
        <Alert
          message={t('admin.fileStorage.orphanFiles.title', 'Orphan Files')}
          description={t(
            'admin.fileStorage.orphanFiles.description',
            'These are files that exist in the database and on disk, but are not linked to any entity (Country, Survey, User, etc.). This may indicate incomplete operations or potential system errors.'
          )}
          type="info"
          showIcon
          icon={<LinkOutlined />}
          style={{ marginBottom: 16 }}
        />
        
        {unassociatedFiles.length === 0 ? (
          <Result
            status="success"
            icon={<CheckCircleOutlined className={styles.successState} />}
            title={t('admin.fileStorage.orphanFiles.empty', 'No Orphan Files Found')}
            subTitle="All files are properly linked to their respective entities."
          />
        ) : (
          <>
            <div className={styles.issueHeader}>
              <div className={styles.issueTitle}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => toggleAllUnassociatedFiles(unassociatedFiles)}
                  style={{ marginRight: 12 }}
                />
                <LinkOutlined style={{ color: '#1890ff', fontSize: 20 }} />
                <span className={`${styles.issueCount}`} style={{ color: '#1890ff' }}>
                  {unassociatedFiles.length}
                </span>
                <Text>orphan files found</Text>
                {selectedUnassociatedFiles.length > 0 && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {selectedUnassociatedFiles.length} selected
                  </Tag>
                )}
              </div>
              {selectedUnassociatedFiles.length > 0 && (
                <Popconfirm
                  title={`Delete ${selectedUnassociatedFiles.length} unassociated file(s)?`}
                  description="This will permanently delete the selected files from disk and database."
                  onConfirm={handleBulkDeleteUnassociatedFiles}
                  okText="Yes, delete selected"
                  okButtonProps={{ danger: true }}
                >
                  <Button 
                    danger 
                    loading={unassociatedDeleteLoading} 
                    icon={<DeleteOutlined />}
                  >
                    Delete Selected ({selectedUnassociatedFiles.length})
                  </Button>
                </Popconfirm>
              )}
            </div>
            
            <List
              className={styles.issueList}
              dataSource={unassociatedFiles}
              renderItem={(item) => (
                <div className={styles.issueItem}>
                  <Checkbox
                    checked={selectedUnassociatedFiles.includes(item.id)}
                    onChange={() => toggleUnassociatedFileSelection(item.id)}
                    style={{ marginRight: 12 }}
                  />
                  <div className={styles.issueItemInfo} style={{ flex: 1 }}>
                    <div className={styles.issueFileName}>
                      {CategoryIcons[item.category] || CategoryIcons.Other}
                      <div className={styles.fileNameContainer} style={{ marginLeft: 8 }}>
                        <span>{item.fileName}</span>
                        <span className={styles.storedFileName}>{item.storedFileName}</span>
                      </div>
                    </div>
                    <div className={styles.issueFileMeta}>
                      ID: {item.id} • {item.fileSizeFormatted} • Uploaded: {getRelativeTime(item.dateCreated)}
                    </div>
                  </div>
                  <Space size="small">
                    <Tooltip title={t('common.view', 'View')}>
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => viewFileDetail(item)}
                      />
                    </Tooltip>
                    <Tooltip title={t('common.download', 'Download')}>
                      <Button
                        type="text"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(item.id, item.fileName)}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="Delete this unassociated file?"
                      description="This will permanently delete the file from disk and database."
                      onConfirm={() => handleDeleteUnassociatedFile(item.id)}
                      okText="Yes, delete"
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title={t('common.delete', 'Delete')}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          loading={unassociatedDeleteLoading}
                          size="small"
                        />
                      </Tooltip>
                    </Popconfirm>
                  </Space>
                </div>
              )}
            />
          </>
        )}
      </div>
    );
  };

  /**
   * Render untracked files tab
   */
  const renderUntrackedFilesTab = () => {
    const untrackedFiles = storageHealth?.untrackedFiles || [];
    
    return (
      <div>
        <Alert
          message={t('admin.fileStorage.untrackedFiles.title', 'Untracked Files')}
          description={t(
            'admin.fileStorage.untrackedFiles.description',
            'These are physical files on disk that have no corresponding database record. They may be leftover from failed uploads or manual file additions.'
          )}
          type="warning"
          showIcon
          icon={<FileUnknownOutlined />}
          style={{ marginBottom: 16 }}
        />
        
        {untrackedFiles.length === 0 ? (
          <Result
            status="success"
            icon={<CheckCircleOutlined className={styles.successState} />}
            title={t('admin.fileStorage.untrackedFiles.empty', 'No Untracked Files Found')}
            subTitle="All files on disk are properly tracked in the database."
          />
        ) : (
          <>
            <div className={styles.issueHeader}>
              <div className={styles.issueTitle}>
                <FileUnknownOutlined style={{ color: '#faad14', fontSize: 20 }} />
                <span className={`${styles.issueCount} ${styles.untrackedCount}`}>
                  {untrackedFiles.length}
                </span>
                <Text>untracked files found</Text>
              </div>
              <Popconfirm
                title="Clean up all untracked files?"
                description="This will permanently delete all files that don't have database records. This cannot be undone."
                onConfirm={handleCleanupUntracked}
                okText="Yes, delete files"
                okButtonProps={{ danger: true }}
              >
                <Button danger loading={cleanupLoading} icon={<DeleteOutlined />}>
                  Clean Up All
                </Button>
              </Popconfirm>
            </div>
            
            <List
              className={styles.issueList}
              dataSource={untrackedFiles}
              renderItem={(item) => (
                <div className={styles.issueItem}>
                  <div className={styles.issueItemInfo}>
                    <div className={styles.issueFileName}>
                      <FileUnknownOutlined style={{ color: '#faad14' }} />
                      <span style={{ marginLeft: 8 }}>{item.fileName}</span>
                    </div>
                    <div className={styles.issueFileMeta}>
                      {item.fileSizeFormatted} • Last modified: {formatTimestamp(item.lastModified)}
                    </div>
                  </div>
                  <Space size="small">
                    <Tooltip title={t('common.view', 'View')}>
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => window.open(filesService.getUntrackedFileUrl(item.fileName), '_blank')}
                      />
                    </Tooltip>
                    <Tooltip title={t('common.download', 'Download')}>
                      <Button
                        type="text"
                        icon={<DownloadOutlined />}
                        onClick={() => filesService.downloadUntrackedFileToBrowser(item.fileName)}
                      />
                    </Tooltip>
                  </Space>
                </div>
              )}
            />
          </>
        )}
      </div>
    );
  };

  /**
   * Render detail modal
   */
  const renderDetailModal = () => (
    <Modal
      title={
        <Space>
          <CloudServerOutlined style={{ color: '#1890ff' }} />
          {t('admin.fileStorage.detail.title', 'File Details')}
        </Space>
      }
      open={detailModalVisible}
      onCancel={() => {
        setDetailModalVisible(false);
        setSelectedFile(null);
      }}
      footer={[
        <Button key="download" icon={<DownloadOutlined />} onClick={() => handleDownload(selectedFile?.id, selectedFile?.fileName)}>
          {t('common.download', 'Download')}
        </Button>,
        <Button key="close" onClick={() => setDetailModalVisible(false)}>
          {t('common.close', 'Close')}
        </Button>,
      ]}
      width={700}
    >
      {selectedFile && (
        <div>
          {/* File Preview */}
          <div className={styles.filePreview}>
            {isPreviewableImage(selectedFile) ? (
              <Image
                src={getFileUrl(selectedFile)}
                alt={selectedFile.altText || selectedFile.fileName}
                className={styles.filePreviewImage}
              />
            ) : (
              <div className={styles.filePreviewPlaceholder}>
                {React.cloneElement(CategoryIcons[selectedFile.category] || CategoryIcons.Other, {
                  style: { fontSize: 48, color: '#8c8c8c' },
                })}
              </div>
            )}
            <div className={styles.filePreviewInfo}>
              <div className={styles.filePreviewName}>{selectedFile.fileName}</div>
              <div className={styles.filePreviewMeta}>
                <Space split={<Divider type="vertical" />}>
                  <span>{selectedFile.fileSizeFormatted}</span>
                  <span>{selectedFile.contentType}</span>
                  <span className={`${styles.categoryBadge} ${getCategoryClass(selectedFile.category)}`}>
                    {selectedFile.category}
                  </span>
                </Space>
              </div>
            </div>
          </div>

          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="ID">{selectedFile.id}</Descriptions.Item>
            <Descriptions.Item label="Extension">{selectedFile.fileExtension}</Descriptions.Item>
            <Descriptions.Item label="Stored Name" span={2}>
              <code>{selectedFile.storedFileName}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Content Type">{selectedFile.contentType}</Descriptions.Item>
            <Descriptions.Item label="Size">
              {selectedFile.fileSizeFormatted} ({selectedFile.fileSizeBytes?.toLocaleString()} bytes)
            </Descriptions.Item>
            
            {(selectedFile.width || selectedFile.height) && (
              <Descriptions.Item label="Dimensions" span={2}>
                {selectedFile.width} × {selectedFile.height} pixels
              </Descriptions.Item>
            )}
            
            <Descriptions.Item label="Hash (SHA256)" span={2}>
              <div className={styles.hashBox}>{selectedFile.hash}</div>
            </Descriptions.Item>
            
            {selectedFile.description && (
              <Descriptions.Item label="Description" span={2}>
                {selectedFile.description}
              </Descriptions.Item>
            )}
            
            {selectedFile.altText && (
              <Descriptions.Item label="Alt Text" span={2}>
                {selectedFile.altText}
              </Descriptions.Item>
            )}
            
            <Descriptions.Item label="Uploaded">
              {formatTimestamp(selectedFile.dateCreated)}
            </Descriptions.Item>
            <Descriptions.Item label="Modified">
              {formatTimestamp(selectedFile.dateModified)}
            </Descriptions.Item>
            
            <Descriptions.Item label="Uploaded By" span={2}>
              {selectedFile.uploadedBy ? (
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <span>{selectedFile.uploadedBy.displayName || selectedFile.uploadedBy.email}</span>
                </Space>
              ) : (
                <Text type="secondary">Unknown</Text>
              )}
            </Descriptions.Item>
            
            <Descriptions.Item label="URL" span={2}>
              <a href={getFileUrl(selectedFile)} target="_blank" rel="noopener noreferrer">
                {selectedFile.relativeUrl}
              </a>
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}
    </Modal>
  );

  return (
    <PagesWrapper>
      <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleSection}>
              <CloudServerOutlined className={styles.titleIcon} />
              <Title level={2} className={styles.title}>
                {t('admin.fileStorage.title', 'File Storage')}
              </Title>
            </div>
            <Text type="secondary">
              {t('admin.fileStorage.description', 'Manage files, monitor storage health, and resolve file integrity issues')}
            </Text>
          </div>

          {/* Statistics Dashboard */}
          {renderStatistics()}

          {/* Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className={styles.tabsSection}
            items={[
              {
                key: 'files',
                label: (
                  <Space>
                    <FolderOpenOutlined />
                    All Files
                    <Badge count={pagination.total} showZero style={{ marginLeft: 8 }} />
                  </Space>
                ),
                children: (
                  <>
                    {/* Toolbar */}
                    <div className={styles.toolbar}>
                      <Space>
                        <Button
                          icon={<FilterOutlined />}
                          onClick={() => setShowFilters(!showFilters)}
                          type={showFilters ? 'primary' : 'default'}
                        >
                          Filters
                        </Button>
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={() => {
                            fetchFiles(pagination.current, pagination.pageSize);
                            fetchStorageHealth();
                          }}
                        >
                          Refresh
                        </Button>
                      </Space>
                    </div>

                    {/* Filters */}
                    {showFilters && renderFilterPanel()}

                    {/* Category Distribution */}
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                      <Col span={24}>
                        {renderCategoryDistribution()}
                      </Col>
                    </Row>

                    {/* Files Table */}
                    <Table
                      columns={columns}
                      dataSource={files}
                      rowKey="id"
                      loading={loading}
                      pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} files`,
                      }}
                      onChange={handleTableChange}
                      scroll={{ x: 1000 }}
                    />
                  </>
                ),
              },
              {
                key: 'ghost',
                label: (
                  <Space>
                    <DisconnectOutlined />
                    Ghost Records
                    <Badge
                      count={storageHealth?.orphanedRecordsCount || 0}
                      style={{ marginLeft: 8, backgroundColor: '#ff4d4f' }}
                    />
                  </Space>
                ),
                children: renderGhostFilesTab(),
              },
              {
                key: 'orphan',
                label: (
                  <Space>
                    <LinkOutlined />
                    Orphan Files
                    <Badge
                      count={storageHealth?.unassociatedFilesCount || 0}
                      style={{ marginLeft: 8, backgroundColor: '#1890ff' }}
                    />
                  </Space>
                ),
                children: renderOrphanFilesTab(),
              },
              {
                key: 'untracked',
                label: (
                  <Space>
                    <FileUnknownOutlined />
                    Untracked Files
                    <Badge
                      count={storageHealth?.untrackedFilesCount || 0}
                      style={{ marginLeft: 8, backgroundColor: '#faad14' }}
                    />
                  </Space>
                ),
                children: renderUntrackedFilesTab(),
              },
              {
                key: 'settings',
                label: (
                  <Space>
                    <HddOutlined />
                    Settings
                  </Space>
                ),
                children: (
                  <Descriptions bordered column={1}>
                    <Descriptions.Item label="Storage Path">
                      <code>{settings?.storagePath || 'N/A'}</code>
                    </Descriptions.Item>
                    <Descriptions.Item label="Max File Size">
                      {settings?.maxFileSizeFormatted || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Allowed Extensions">
                      <Space wrap>
                        {(settings?.allowedExtensions || []).map(ext => (
                          <Tag key={ext}>{ext}</Tag>
                        ))}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Health Check">
                      {storageHealth?.checkedAt ? formatTimestamp(storageHealth.checkedAt) : 'Never'}
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
            ]}
          />

        {/* Detail Modal */}
        {renderDetailModal()}
      </div>
    </PagesWrapper>
  );
};

export default FileStorage;
